import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { useAppVersion } from '@/lib/useAppVersion';
import Constants from 'expo-constants';
import * as React from 'react';
import { AlertTriangleIcon, ExternalLinkIcon, RefreshCwIcon } from 'lucide-react-native';
import { Linking, Platform, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const APP_STORE_URL = 'https://apps.apple.com/us/app/radious-local-events/id6760730711';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.radious';
const APP_VERSION = Constants.nativeAppVersion ?? Constants.expoConfig?.version ?? '';
const STORE_NAME = Platform.OS === 'android' ? 'Play Store' : 'App Store';

function openAppStore() {
  const storeURL = Platform.OS === 'android' ? PLAY_STORE_URL : APP_STORE_URL;

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.assign(APP_STORE_URL);
    return;
  }

  Linking.openURL(storeURL).catch(() => undefined);
}

export default function UpdateRequiredScreen() {
  const versionQuery = useAppVersion();
  const minSupported = versionQuery.data?.min_supported?.trim();
  const latest = versionQuery.data?.latest?.trim();
  const message =
    versionQuery.data?.update_message?.trim()
      ? versionQuery.data.update_message.trim()
      : `A new version of Radious is ready in the ${STORE_NAME}.`;
  const requiredVersion = minSupported || latest || 'Latest';
  const latestVersion = latest || requiredVersion;

  return (
    <SafeAreaView className="flex-1 bg-background">
      <View className="flex-1 justify-center px-6">
        <View className="w-full max-w-md self-center rounded-lg border border-border bg-card">
          <View className="border-b border-border p-5">
            <View className="mb-4 flex-row items-center gap-3">
              <View className="size-9 items-center justify-center rounded-md border border-destructive/20 bg-destructive/10">
                <Icon as={AlertTriangleIcon} size={18} className="text-destructive" />
              </View>
              <View className="flex-1">
                <Text className="text-base font-semibold text-foreground">Update Radious</Text>
                <Text variant="muted" className="mt-1">
                  This version can’t continue.
                </Text>
              </View>
            </View>

            <Text className="leading-6 text-muted-foreground">{message}</Text>
          </View>

          <View className="px-5 py-4">
            <VersionRow label="Installed" value={formatVersion(APP_VERSION)} />
            <VersionRow label="Required" value={formatVersion(requiredVersion)} emphasis />
            <VersionRow label="Latest" value={formatVersion(latestVersion)} />
          </View>

          <View className="border-t border-border px-5 py-4">
            <Text variant="muted" className="leading-5">
              Some updates can wait. This one keeps the app aligned with current event, venue, and
              account data.
            </Text>
          </View>

          <View className="gap-2 border-t border-border p-5">
            <Button className="w-full" size="lg" onPress={openAppStore}>
              <Icon as={ExternalLinkIcon} size={16} className="text-primary-foreground" />
              <Text>Open {STORE_NAME}</Text>
            </Button>
            <Button
              className="w-full"
              size="lg"
              variant="outline"
              disabled={versionQuery.isFetching}
              onPress={() => {
                versionQuery.refetch().catch(() => undefined);
              }}>
              <Icon as={RefreshCwIcon} size={16} className="text-foreground" />
              <Text>{versionQuery.isFetching ? 'Checking...' : 'Check again'}</Text>
            </Button>
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
}

function VersionRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <View className="flex-row items-center justify-between border-b border-border py-3 last:border-b-0">
      <Text variant="muted">{label}</Text>
      <Text className={emphasis ? 'font-semibold text-foreground' : 'font-medium text-foreground'}>
        {value}
      </Text>
    </View>
  );
}

function formatVersion(version: string) {
  const trimmed = version.trim();
  if (!trimmed) {
    return 'Unknown';
  }
  if (trimmed.toLowerCase() === 'latest') {
    return 'Latest';
  }
  return trimmed.startsWith('v') ? trimmed : `v${trimmed}`;
}
